import React, { useState } from 'react';
import { services } from '../data/mockData';
import { CheckCircle, ArrowRight, Filter } from 'lucide-react';

const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  return (
    <div className="min-h-screen pt-24 bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Our Services
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Comprehensive solar solutions tailored to your specific needs across all sectors
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-3xl font-bold text-white mb-2">{service.title}</h3>
                  </div>
                </div>

                <div className="p-8">
                  <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="mb-6">
                    <button
                      onClick={() => setExpandedService(expandedService === service.id ? null : service.id)}
                      className="flex items-center text-[#0C6951] font-semibold hover:text-[#004534] transition-colors mb-4"
                    >
                      <span className="mr-2">
                        {expandedService === service.id ? 'Hide Features' : 'View All Features'}
                      </span>
                      <ArrowRight
                        className={`w-5 h-5 transition-transform duration-300 ${
                          expandedService === service.id ? 'rotate-90' : ''
                        }`}
                      />
                    </button>

                    {expandedService === service.id && (
                      <div className="space-y-3 animate-slideDown">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center space-x-2">
                    <span>Request Service</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Our Service Process
            </h2>
            <p className="text-xl text-gray-600">
              Simple, transparent, and hassle-free from start to finish
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {[
              {
                step: '01',
                title: 'Consultation',
                description: 'Free site visit and energy audit',
              },
              {
                step: '02',
                title: 'Design',
                description: 'Customized solar solution design',
              },
              {
                step: '03',
                title: 'Proposal',
                description: 'Detailed quotation and ROI analysis',
              },
              {
                step: '04',
                title: 'Installation',
                description: 'Professional setup by certified team',
              },
              {
                step: '05',
                title: 'Support',
                description: 'Ongoing maintenance and monitoring',
              },
            ].map((process, index) => (
              <div
                key={index}
                className="relative bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] text-white font-bold text-3xl w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  {process.step}
                </div>
                <h3 className="text-xl font-bold text-[#004534] mb-2">{process.title}</h3>
                <p className="text-gray-600 text-sm">{process.description}</p>
                {index < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-[#1e3a8a] to-[#22c55e]"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Service Benefits
            </h2>
            <p className="text-xl text-gray-600">
              Why thousands choose us for their solar needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Turnkey Solutions',
                description: 'Complete end-to-end service from design to commissioning. We handle everything so you don\'t have to worry.',
              },
              {
                title: 'Expert Team',
                description: '20+ years of experience with certified professionals. Quality workmanship guaranteed on every project.',
              },
              {
                title: 'After-Sales Support',
                description: 'Comprehensive maintenance and 24/7 support. We\'re always there when you need us.',
              },
              {
                title: 'Government Assistance',
                description: 'Help with subsidies, approvals, and net metering. Maximize your savings with available incentives.',
              },
              {
                title: 'Customized Design',
                description: 'Solutions tailored to your specific requirements. No two projects are the same.',
              },
              {
                title: 'Transparent Pricing',
                description: 'No hidden costs or surprises. Clear quotations with detailed ROI analysis.',
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] w-12 h-12 rounded-full flex items-center justify-center mb-4 shadow-md">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#004534] mb-3">{benefit.title}</h3>
                <p className="text-gray-700 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Solar Journey?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Get a free consultation and customized quote for your solar project
          </p>
          <button className="inline-flex items-center space-x-2 bg-[#D3FF62] hover:bg-[#c4f052] text-[#004534] px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 hover:scale-105 shadow-2xl">
            <span>Schedule Free Consultation</span>
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </section>

      <style jsx>{`
        .solar-grid-bg {
          background-image: 
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px);
          background-size: 80px 80px;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Services;
